import React, { useEffect, useState } from 'react';
import DsButton from '../../common/DsButton';
import DsModal from '../../common/DsModal';
import { SkeletonLine } from '../../common/Skeleton';
import DsSelect from '../../common/DsSelect';

export function ImageFilePreview({ file, alt, className = '' }) {
  const [previewUrl, setPreviewUrl] = useState('');

  useEffect(() => {
    if (!file) {
      setPreviewUrl('');
      return undefined;
    }
    const objectUrl = URL.createObjectURL(file);
    setPreviewUrl(objectUrl);
    return () => {
      URL.revokeObjectURL(objectUrl);
    };
  }, [file]);

  if (!file || !previewUrl) return null;
  return <img src={previewUrl} alt={alt || 'preview'} className={className} />;
}

export function BroadcastEventSelectModal({
  open,
  onClose,
  onClearSelectedEvent,
  eventSearchKeyword,
  setEventSearchKeyword,
  eventSearchStartDate,
  setEventSearchStartDate,
  eventSearchEndDate,
  setEventSearchEndDate,
  searchEventCandidates,
  eventSearchLoading,
  eventSearchLimit,
  eventSearchHasMore,
  eventSearchResults,
  selectedEventId,
  buildEventOptionLabel,
  handleSelectEvent,
  loadingFallback,
}) {
  return (
    <DsModal
      open={open}
      onClose={onClose}
      title="イベント選択"
      size="lg"
      className="admin-broadcast-event-modal"
      footer={(
        <>
          <DsButton onClick={onClose}>
            閉じる
          </DsButton>
          <DsButton variant="secondary" onClick={onClearSelectedEvent}>
            選択中イベントを解除
          </DsButton>
        </>
      )}
    >
      <div className="admin-broadcast-event-search-form">
        <label>
          自由入力
          <input
            type="text"
            value={eventSearchKeyword}
            onChange={(event) => setEventSearchKeyword(event.target.value)}
            placeholder="イベント名 / 公演名 / 会場名"
          />
        </label>
        <label>
          期間開始
          <input
            type="date"
            value={eventSearchStartDate}
            onChange={(event) => setEventSearchStartDate(event.target.value)}
          />
        </label>
        <label>
          期間終了
          <input
            type="date"
            value={eventSearchEndDate}
            onChange={(event) => setEventSearchEndDate(event.target.value)}
          />
        </label>
      </div>
      <DsButton
        className="admin-broadcast-event-search-action"
        onClick={searchEventCandidates}
        loading={eventSearchLoading}
      >
        {eventSearchLoading ? '検索中...' : '検索'}
      </DsButton>
      <div className="admin-broadcast-event-search-hint">
        <span>検索は条件ごとにDBから取得します（最大 {eventSearchLimit} 件）</span>
        {eventSearchHasMore && <span>条件に一致するイベントがまだあります。条件を絞ってください。</span>}
      </div>
      {eventSearchLoading ? (
        loadingFallback || <SkeletonLine width="72%" height={14} />
      ) : (
        <div className="admin-broadcast-event-search-results">
          {eventSearchResults.length === 0 ? (
            <p className="admin-broadcast-empty">条件に一致するイベントがありません。</p>
          ) : (
            eventSearchResults.map((event) => {
              const eventUuid = String(event?.event_uuid || '');
              const isSelected = eventUuid && String(selectedEventId) === eventUuid;
              return (
                <button
                  key={eventUuid || `${event?.name || 'event'}-${event?.event_date || 'na'}`}
                  type="button"
                  className={`admin-broadcast-event-result-row${isSelected ? ' is-selected' : ''}`}
                  onClick={() => handleSelectEvent(event)}
                >
                  <span className="admin-broadcast-event-result-title">{buildEventOptionLabel(event)}</span>
                  {String(event?.venue || '').trim() && (
                    <span className="admin-broadcast-event-result-meta">会場: {event.venue}</span>
                  )}
                </button>
              );
            })
          )}
        </div>
      )}
    </DsModal>
  );
}

export function BroadcastCheckboxModal({
  open,
  onClose,
  title,
  clearLabel,
  onClear,
  options,
  selectedValues,
  onToggle,
}) {
  return (
    <DsModal
      open={open}
      onClose={onClose}
      title={title}
      footer={(
        <>
          <DsButton onClick={onClose}>選択を確定</DsButton>
          <DsButton variant="secondary" onClick={onClear}>{clearLabel}</DsButton>
        </>
      )}
    >
      <div className="admin-broadcast-modal-checkbox-grid">
        {options.map((option) => {
          const value = String(option?.value || '');
          if (!value) return null;
          return (
            <label key={value} className="admin-broadcast-modal-checkbox-item">
              <input type="checkbox" checked={selectedValues.includes(value)} onChange={() => onToggle(value)} />
              <span>{option.label || value}</span>
            </label>
          );
        })}
      </div>
    </DsModal>
  );
}

export function BroadcastMessageDraftList({
  messageDrafts,
  sending,
  messageLimit,
  imageAccept,
  fileAccept,
  maxItems,
  maxFileSizeMb,
  maxTotalFileSizeMb,
  updateMessageDraftType,
  updateMessageDraftContent,
  updateMessageDraftFile,
  removeMessageDraft,
  addMessageDraft,
}) {
  return (
    <>
      <div className="admin-broadcast-draft-list">
        {messageDrafts.map((draft, index) => (
          <div key={draft.id} className="admin-broadcast-draft-item">
            <div className="admin-broadcast-draft-header">
              <span className="admin-broadcast-draft-title">送信メッセージ {index + 1}</span>
              <DsSelect
                value={draft.type}
                onChange={(event) => updateMessageDraftType(draft.id, event.target.value)}
                disabled={sending}
              >
                <option value="text">テキスト</option>
                <option value="image">画像</option>
                <option value="file">ファイル</option>
              </DsSelect>
              <DsButton
                variant="danger"
                size="sm"
                className="admin-broadcast-draft-remove"
                onClick={() => removeMessageDraft(draft.id)}
                disabled={sending || messageDrafts.length <= 1}
              >
                削除
              </DsButton>
            </div>
            {draft.type === 'text' ? (
              <textarea
                value={draft.content}
                onChange={(event) => updateMessageDraftContent(draft.id, event.target.value)}
                maxLength={messageLimit}
                rows={4}
                placeholder="送信するメッセージを入力してください"
                disabled={sending}
              />
            ) : (
              <div className="admin-broadcast-draft-file-row">
                <input
                  type="file"
                  accept={draft.type === 'image' ? imageAccept : fileAccept}
                  onChange={(event) => updateMessageDraftFile(draft.id, event.target.files?.[0] || null)}
                  disabled={sending}
                />
                <span className="admin-broadcast-draft-file-name">
                  {draft.file ? draft.file.name : 'ファイル未選択'}
                </span>
                <span className="admin-broadcast-draft-file-hint">
                  1件あたり {maxFileSizeMb}MB まで / 合計 {maxTotalFileSizeMb}MB まで
                </span>
                {draft.type === 'image' && draft.file && (
                  <div className="admin-broadcast-draft-image-preview-wrap">
                    <ImageFilePreview
                      file={draft.file}
                      alt={draft.file.name || 'preview'}
                      className="admin-broadcast-draft-image-preview"
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
      <DsButton
        variant="secondary"
        className="admin-broadcast-draft-add"
        onClick={addMessageDraft}
        disabled={sending || messageDrafts.length >= maxItems}
      >
        送信メッセージを追加
      </DsButton>
    </>
  );
}

export function BroadcastConfirmModal({
  open,
  onClose,
  sending,
  onSubmit,
  selectedCount,
  messageDrafts,
}) {
  return (
    <DsModal
      open={open}
      onClose={onClose}
      title="Step3. 確認"
      dismissible={!sending}
      footer={(
        <>
          <DsButton onClick={onSubmit} loading={sending}>
            {sending ? '送信中...' : '送信'}
          </DsButton>
          <DsButton variant="secondary" onClick={onClose} disabled={sending}>
            戻る
          </DsButton>
        </>
      )}
    >
      <p className="admin-broadcast-confirm-count">送信先件数: {selectedCount}件</p>
      <div className="admin-broadcast-modal-message">
        <p>送信メッセージプレビュー（順序送信）:</p>
        <div className="admin-broadcast-chat-preview">
          {messageDrafts.map((draft, index) => (
            <div key={draft.id} className="admin-broadcast-chat-preview-row">
              <span className="admin-broadcast-chat-preview-seq">{index + 1}</span>
              <div className={`admin-broadcast-chat-bubble is-${draft.type}`}>
                {draft.type === 'text' ? (
                  <p className="admin-broadcast-chat-bubble-text">
                    {String(draft.content || '').trim() || '（本文なし）'}
                  </p>
                ) : draft.type === 'image' ? (
                  <div className="admin-broadcast-chat-bubble-asset">
                    <span className="admin-broadcast-chat-bubble-asset-type">画像</span>
                    {draft.file ? (
                      <ImageFilePreview
                        file={draft.file}
                        alt={draft.file.name || 'preview'}
                        className="admin-broadcast-chat-bubble-image"
                      />
                    ) : (
                      <span className="admin-broadcast-chat-bubble-asset-name">未選択</span>
                    )}
                  </div>
                ) : (
                  <div className="admin-broadcast-chat-bubble-asset">
                    <span className="admin-broadcast-chat-bubble-asset-type">ファイル</span>
                    <span className="admin-broadcast-chat-bubble-asset-name">
                      {draft.file?.name || '未選択'}
                    </span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </DsModal>
  );
}
